// components/hrAdmin/documentManager/AddCategory.js - Updated with new fields
import React, { useState } from 'react'
import { apiPost } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import { useForm } from 'react-hook-form'
import useToastContext from 'hooks/useToastContext'
import { useTranslation } from 'react-i18next'
import OInputField from 'components/reusable/OInputField'
import OTextArea from 'components/reusable/OTextArea'
import OSelect from 'components/reusable/OSelect'
import { preventMaxInput } from 'utils/validations'
import formValidation from '../../utils/formValidation'

const AddCategory = ({ fetchCategories, handleCategory, user, fetchCategoriesForDropdown }) => {
  const { t } = useTranslation()
  const notification = useToastContext()
  const [isLoading, setIsLoading] = useState(false)
  
  const yesNoOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ]

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      name: '',
      description: '',
      hasMandatoryExpiry: 'no',
      requiresElectronicSignature: 'no'
    }
  })

  const handleSubmitForm = async (data) => {
    try {
      setIsLoading(true)
      const payload = {
        name: data?.name?.trim(),
        description: data?.description?.trim(),
        hasMandatoryExpiry: data.hasMandatoryExpiry,
        requiresElectronicSignature: data.requiresElectronicSignature,
        companyID: user?.companyID || ''
      }
      const res = await apiPost(apiPath.categoryAdd, payload)
      if (res?.data?.success) {
        fetchCategories()
        fetchCategoriesForDropdown()
        handleCategory(false)
        notification.success(res.data?.message)
      } else {
        notification.error(res?.data?.message)
      }
    } catch (err) {
      console.log('err:', err)
      notification.error(err.message || t('SOMETHING_WENT_WRONG'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="overflow-x-hidden overflow-y-auto fixed inset-0 justify-center items-center flex z-50 outline-none focus:outline-none">
        <div className="relative mx-auto max-w-3xl w-auto my-6">
          <div className="sm:px-2 sm:py-8 px-7 md:py-4">
            <div className="border-0 rounded-lg flex-col w-full bg-white shadow-lg relative flex outline-none focus:outline-none min-w-[550px]">
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">{t("ADD_CATEGORY")}</h3>
                <button 
                  className="ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                  onClick={() => handleCategory(false)}
                >
                  <span
                    title="Close"
                    className="text-[#B8BBBF] text-4xl"
                  >
                    ×
                  </span>
                </button>
              </div>
              <form method="post" onSubmit={handleSubmit(handleSubmitForm)}>
                <div className="flex-auto relative p-6">
                  <div className="space-y-6">
                    {/* Category Name */}
                    <div className="relative z-0 w-full group">
                      <OInputField
                        name="name"
                        id="name"
                        autoFocus={true}
                        wrapperClassName="relative z-0 w-full group"
                        type="text"
                        inputLabel={
                          <>
                            {t("CATEGORY_NAME")}
                            <span className="text-red-500">*</span>
                          </>
                        }
                        onInput={(e) => preventMaxInput(e, 100)}
                        maxLength={100}
                        register={register(
                          "name",
                          formValidation["categoryName"]
                        )}
                        errors={errors}
                      />
                    </div>

                    {/* Category Description */}
                    <div className="relative z-0 w-full group">
                      <OTextArea
                        name="description"
                        id="description"
                        wrapperClassName="relative z-0 w-full group"
                        inputLabel={t("DESCRIPTION")}
                        onInput={(e) => preventMaxInput(e, 500)}
                        maxLength={500}
                        rows={4}
                        register={register("description")}
                        errors={errors}
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      {/* Mandatory Expiry Date for Documents in this Category */}
                      <div className="relative z-0 w-full group">
                        <OSelect
                          name="hasMandatoryExpiry"
                          id="hasMandatoryExpiry"
                          wrapperClassName="relative z-0 w-full group"
                          inputLabel={
                            <>
                              {t("MANDATORY_EXPIRY_DATE")}
                              <span className="text-red-500">*</span>
                            </>
                          }
                          options={yesNoOptions}
                          value={watch('hasMandatoryExpiry')}
                          onChange={(value) => setValue('hasMandatoryExpiry', value)}
                          register={register('hasMandatoryExpiry', {
                            required: t('PLEASE_SELECT_EXPIRY_OPTION')
                          })}
                          errors={errors}
                        />
                      </div>

                      {/* Electronic Signature Required for Documents in this Category */}
                      <div className="relative z-0 w-full group">
                        <OSelect
                          name="requiresElectronicSignature"
                          id="requiresElectronicSignature"
                          wrapperClassName="relative z-0 w-full group"
                          inputLabel={
                            <>
                              {t("ELECTRONIC_SIGNATURE_REQUIRED")}
                              <span className="text-red-500">*</span>
                            </>
                          }
                          options={yesNoOptions}
                          value={watch('requiresElectronicSignature')}
                          onChange={(value) => setValue('requiresElectronicSignature', value)}
                          register={register('requiresElectronicSignature', {
                            required: t('PLEASE_SELECT_SIGNATURE_OPTION')
                          })}
                          errors={errors}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Footer */}
                <div className="border-t border-solid flex items-center justify-center p-6 border-slate-200 rounded-b">
                  <button
                    type="button"
                    onClick={() => handleCategory(false)}
                    className="font-normal px-12 py-2.5 text-black bg-[#E1E1E1] text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150"
                    disabled={isLoading}
                  >
                    {t("O_BACK")}
                  </button>

                  <button
                    type="submit"
                    className="rounded shadow hover:shadow-lg outline-none bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 focus:outline-none mr-1 ease-linear transition-all duration-150"
                    disabled={isLoading}
                  >
                    {isLoading ? t('ADDING...') : t('O_ADD')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  )
}

export default AddCategory